// Task ERRTOAST: turn whatever the platform threw into a string a Snackbar can actually render.
//
// Every Perps V1 screen used to report errors as
//
//   notify(err?.body?.message || err?.message || translate('…'), { type: 'error' })
//
// which quietly assumes `body.message` is a string. It is not: admin-service mounts a global
// NestJS `ValidationPipe` (see xbit-admin-service `src/main.ts`), and its BadRequestException body
// carries an ARRAY of per-constraint strings, which the GlobalExceptionFilter forwards verbatim:
//
//   { "code": "Bad Request Exception",
//     "message": ["code must match /^[a-z0-9_]{1,31}$/ regular expression"] }
//
// A non-empty array is truthy, so it won the `||` chain — and react-admin's Notification renders
// `message && typeof message === 'string' && translate(message)`. A non-string collapses to
// `false`, so the admin got a red toast with no text at all, on every field-level validation
// error in the console.
//
// The irony worth remembering: `error.message` — the very next link in that chain — would have
// rendered fine, because AppDataProvider's httpClient stringifies it (`new HttpError(message + '',
// …)`). The broken branch shadowed the working one.
//
// Contract: always returns a non-empty string, never an array and never undefined — otherwise the
// bug just moves somewhere else.

// Multi-constraint failures are joined onto one line: an admin fixing two fields at once has to
// see both, and MUI's Snackbar is single-line unless `multiLine` is set.
const JOIN = '; ';

// Unreachable in practice (every caller passes a translated fallback), but it keeps the function
// total. It is a react-admin core key, so notify()'s own translate() resolves it in en and zh.
const LAST_RESORT = 'ra.notification.http_error';

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Extract a renderable message from a caught error, in this order:
 *   1. `body.message` as a string array (joined),
 *   2. `body.message` as a plain string,
 *   3. `error.message`,
 *   4. the caller's fallback.
 *
 * @param error    the caught value — typed `unknown` because a catch block's is
 * @param fallback already-translated text to show when the error carries nothing usable
 */
export function errorMessage(error: unknown, fallback: string): string {
  const err = error as { body?: { message?: unknown }; message?: unknown } | null | undefined;
  const bodyMessage = err?.body?.message;

  if (Array.isArray(bodyMessage)) {
    // The platform's shape is not ours to rely on: drop anything that isn't usable text rather
    // than assuming every entry is a string and stringifying an accidental `[object Object]`
    // into the admin's face.
    const parts = bodyMessage.map(clean).filter(Boolean);
    if (parts.length > 0) return parts.join(JOIN);
  } else {
    // Upstream GraphQL errors arrive as { code: 'GRAPHQL_ERROR', message: '<upstream text>' } with
    // `message` a plain string; those toasts already read correctly and must keep reading the same.
    const single = clean(bodyMessage);
    if (single) return single;
  }

  return clean(err?.message) || clean(fallback) || LAST_RESORT;
}
